<template>
  <div class="step-content">
    <!-- Message Preview -->
    <div class="message-preview-final">
      <h4>Message Preview</h4>

      <!-- Language Tabs -->
      <div class="language-tabs">
        <button
          type="button"
          class="language-tab"
          :class="{ active: activeLanguage === 'en' }"
          @click="activeLanguage = 'en'"
        >
          <i class="fa fa-language"></i>
          English
          <i 
            v-if="!isLanguageValid('en')" 
            class="fa fa-exclamation-circle validation-icon"
            title="Content missing"
          ></i>
          <i 
            v-else 
            class="fa fa-check-circle validation-icon valid"
            title="Complete"
          ></i>
        </button>
        <button
          type="button"
          class="language-tab"
          :class="{ active: activeLanguage === 'fr' }"
          @click="activeLanguage = 'fr'"
        >
          <i class="fa fa-language"></i>
          Français
          <i 
            v-if="!isLanguageValid('fr')" 
            class="fa fa-exclamation-circle validation-icon"
            title="Content missing"
          ></i>
          <i 
            v-else 
            class="fa fa-check-circle validation-icon valid"
            title="Complete"
          ></i>
        </button>
      </div>

      <div class="preview-card">
        <div class="preview-header">
          <div class="preview-subject">
            {{ currentSubject || "No subject" }}
          </div>
          <div
            class="preview-priority"
            :class="'priority-' + messageType"
          >
            <i :class="getPriorityIcon(messageType)"></i>
            {{ messageType.charAt(0).toUpperCase() + messageType.slice(1) }}
          </div>
        </div>
        <div class="preview-body">
          <div v-if="currentBody" v-html="currentBody"></div>
          <div v-else class="no-content">No message content</div>
        </div>

        <!-- Response Buttons -->
        <div
          v-if="responseType === 'ok_not_ok'"
          class="preview-response-buttons"
        >
          <div class="response-buttons-header">
            <small>Recipients will see these response options:</small>
          </div>
          <div class="response-buttons">
            <button class="response-btn response-btn-ok" disabled>
              <i class="fa fa-check"></i>
              OK
            </button>
            <button class="response-btn response-btn-not-ok" disabled>
              <i class="fa fa-exclamation"></i>
              Not OK
            </button>
          </div>
        </div>

        <div class="preview-footer">
          <small
            >This message will be sent to
            {{ contactsCount + ccEmailsCount }} recipients</small
          >
          <small
            v-if="responseType === 'ok_not_ok'"
            class="response-type-info"
          >
            <i class="fa fa-reply"></i>
            Recipients can respond with OK/Not OK
          </small>
        </div>
      </div>
    </div>

    <!-- Message Details -->
    <div class="message-details-preview">
      <h4>Message Details</h4>
      <div class="details-grid">
        <div class="detail-item">
          <div class="detail-label">
            <i class="fa fa-calendar-check"></i> Event
          </div>
          <div class="detail-value">
            {{ eventName || 'Not selected' }}
          </div>
        </div>
        <div class="detail-item">
          <div class="detail-label">
            <i class="fa fa-tag"></i> Message Type
          </div>
          <div class="detail-value">
            {{ messageTypeName || 'Not selected' }}
          </div>
        </div>
        <div class="detail-item">
          <div class="detail-label">
            <i class="fa fa-exclamation-triangle"></i> Crisis Type
          </div>
          <div class="detail-value">
            {{ crisisTypeName || 'Not selected' }}
          </div>
        </div>
        <div class="detail-item">
          <div class="detail-label">
            <i class="fa fa-flag"></i> Priority
          </div>
          <div class="detail-value">
            <span class="priority-badge" :class="'priority-' + messageType">
              {{ messageType.charAt(0).toUpperCase() + messageType.slice(1) }}
            </span>
          </div>
        </div>
        <div class="detail-item">
          <div class="detail-label">
            <i class="fa fa-reply"></i> Response Type
          </div>
          <div class="detail-value">
            {{ responseType === 'ok_not_ok' ? 'OK/Not OK' : 'None' }}
          </div>
        </div>
      </div>
    </div>

    <!-- Recipients Preview -->
    <div class="recipients-preview">
      <h4>Recipients ({{ contactsCount + ccEmailsCount }} total)</h4>
      <div class="recipients-chips-container">
        <!-- Contact Recipients -->
        <div v-if="contactsCount > 0">
          <div class="recipients-section-label">
            <i class="fa fa-users"></i> Contacts ({{ contactsCount }})
          </div>
          <div class="recipients-chips">
            <span
              v-for="contact in contacts"
              :key="contact.id"
              class="recipient-chip"
            >
              <i class="fa fa-user"></i>
              {{ contact.name }}
            </span>
          </div>
        </div>

        <!-- CC Email Recipients -->
        <div v-if="ccEmailsCount > 0" style="margin-top: 16px;">
          <div class="recipients-section-label">
            <i class="fa fa-envelope"></i> CC Emails ({{ ccEmailsCount }})
          </div>
          <div class="recipients-chips">
            <span
              v-for="(email, index) in ccEmails"
              :key="`cc-${index}`"
              class="recipient-chip cc-chip"
            >
              <i class="fa fa-envelope-o"></i>
              {{ email }}
            </span>
          </div>
        </div>

        <!-- No Recipients Message -->
        <div v-if="contactsCount === 0 && ccEmailsCount === 0" class="no-recipients">
          <i class="fa fa-exclamation-circle"></i>
          No recipients selected
        </div>
      </div>
    </div>

    <!-- Send Options -->
    <div class="send-options">
      <h4>Send Options</h4>
      <div class="option-cards">
        <div
          class="option-card"
          :class="{ active: selectedSendOption === 'send_now', disabled: isAutomaticMessageType }"
          @click="!isAutomaticMessageType && $emit('update:selectedSendOption', 'send_now')"
        >
          <div class="option-icon">
            <i class="fa fa-paper-plane"></i>
          </div>
          <div class="option-content">
            <h5>Send Now</h5>
            <p v-if="!isAutomaticMessageType">
              Send the message immediately to all selected recipients
            </p>
            <p v-else class="disabled-reason">
              Only available for specific message types
            </p>
          </div>
        </div>

        <div
          class="option-card"
          :class="{ active: selectedSendOption === 'send_scheduled', disabled: !isAutomaticMessageType }"
          @click="isAutomaticMessageType && $emit('update:selectedSendOption', 'send_scheduled')"
        >
          <div class="option-icon">
            <i class="fa fa-clock"></i>
          </div>
          <div class="option-content">
            <h5>Schedule Send</h5>
            <p v-if="isAutomaticMessageType">
              Schedule the message to be sent at a specific time
            </p>
            <p v-else class="disabled-reason">
              Only available for automatic message type
            </p>
          </div>
        </div>

        <div
          class="option-card disabled"
          :class="{ active: selectedSendOption === 'draft' }"
        >
          <div class="option-icon">
            <i class="fa fa-save"></i>
          </div>
          <div class="option-content">
            <h5>Draft</h5>
            <p>
              Save the message as a draft for later review and sending
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PreviewSendStep",
  props: {
    messageSubject: { type: String, required: true },
    messageContent: { type: String, required: true },
    messageType: { type: String, required: true },
    responseType: { type: String, required: true },
    selectedSendOption: { type: String, required: true },
    messageTypeName: { type: String, default: '' },
    crisisTypeName: { type: String, default: '' },
    eventName: { type: String, default: '' },
    contacts: { type: Array, required: true },
    ccEmails: { type: Array, required: true },
    messageContents: { type: Array, default: () => [] },
    selectedMessageType: { type: String, default: '' },
  },
  data() {
    return {
      activeLanguage: 'en',
    };
  },
  watch: {
    isAutomaticMessageType: {
      immediate: true,
      handler(isAutomatic) {
        // Auto-select the first active send option based on message type
        if (isAutomatic) {
          // For automatic message type, only "send_scheduled" is available
          if (this.selectedSendOption !== 'send_scheduled') {
            this.$emit('update:selectedSendOption', 'send_scheduled');
          }
        } else {
          // For specific message type, only "send_now" is available
          if (this.selectedSendOption !== 'send_now') {
            this.$emit('update:selectedSendOption', 'send_now');
          }
        }
      }
    }
  },
  computed: {
    isAutomaticMessageType() {
      return !this.selectedMessageType || this.selectedMessageType === '' || this.selectedMessageType === 'auto';
    },
    contactsCount() {
      return this.contacts.length;
    },
    ccEmailsCount() {
      return this.ccEmails.length;
    },
    currentSubject() {
      if (this.messageContents && this.messageContents.length > 0) {
        const langCode = this.activeLanguage.toUpperCase();
        const subjectContent = this.messageContents.find(
          c => c.languageCode === langCode && c.contentType === 'message.subject'
        );
        return subjectContent ? subjectContent.content : '';
      }
      // Fallback to legacy props for English only
      return this.activeLanguage === 'en' ? this.messageSubject : '';
    },
    currentBody() {
      if (this.messageContents && this.messageContents.length > 0) {
        const langCode = this.activeLanguage.toUpperCase();
        const bodyContent = this.messageContents.find(
          c => c.languageCode === langCode && c.contentType === 'message.body'
        );
        return bodyContent ? bodyContent.content : '';
      }
      // Fallback to legacy props for English only
      return this.activeLanguage === 'en' ? this.messageContent : '';
    },
  },
  methods: {
    getPriorityIcon(priority) {
      const icons = {
        low: "fa fa-info-circle",
        medium: "fa fa-exclamation-triangle",
        high: "fa fa-exclamation-circle",
        urgent: "fa fa-exclamation",
      };
      return icons[priority] || "fa fa-info-circle";
    },
    isLanguageValid(language) {
      if (this.messageContents && this.messageContents.length > 0) {
        const langCode = language.toUpperCase();
        const subject = this.messageContents.find(
          c => c.languageCode === langCode && c.contentType === 'message.subject'
        );
        const body = this.messageContents.find(
          c => c.languageCode === langCode && c.contentType === 'message.body'
        );
        
        const subjectValid = subject && subject.content && subject.content.trim().length > 0;
        const bodyValid = body && body.content && this.getPlainTextFromHTML(body.content).trim().length > 0;
        
        return subjectValid && bodyValid;
      }
      // Fallback validation for legacy props
      return language === 'en' && this.messageSubject && this.messageContent;
    },
    getPlainTextFromHTML(html) {
      if (!html) return '';
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    },
  },
};
</script>

<style scoped>
.message-preview-final {
  margin-bottom: 32px;
}

.message-preview-final h4,
.message-details-preview h4,
.recipients-preview h4,
.send-options h4 {
  color: #2c3e50;
  margin-bottom: 16px;
  font-size: 16px;
}

/* Language Tabs */
.language-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 0;
  border-bottom: 2px solid #e9ecef;
}

.language-tab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  color: #6c757d;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  bottom: -2px;
}

.language-tab:hover {
  color: #007bff;
  background: #f8f9fa;
}

.language-tab.active {
  color: #007bff;
  border-bottom-color: #007bff;
  background: white;
}

.language-tab i.fa-language {
  font-size: 16px;
}

.validation-icon {
  font-size: 14px;
  margin-left: 4px;
  color: #dc3545;
}

.validation-icon.valid {
  color: #28a745;
}

.preview-card {
  background: white;
  border: 2px solid #e9ecef;
  border-top: none;
  border-radius: 0 0 8px 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.preview-header {
  background: #f8f9fa;
  padding: 16px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.preview-subject {
  font-weight: 600;
  color: #2c3e50;
  font-size: 16px;
}

.preview-priority {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.priority-low {
  background: #d1ecf1;
  color: #0c5460;
}

.priority-medium {
  background: #fff3cd;
  color: #856404;
}

.priority-high {
  background: #f8d7da;
  color: #721c24;
}

.priority-urgent {
  background: #f5c6cb;
  color: #721c24;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
  }
}

.preview-body {
  padding: 20px;
  color: #495057;
  line-height: 1.6;
  font-size: 14px;
}

.preview-body .no-content {
  color: #6c757d;
  font-style: italic;
}

.preview-response-buttons {
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
  padding: 16px 20px;
}

.response-buttons-header {
  text-align: center;
  margin-bottom: 12px;
}

.response-buttons-header small {
  color: #6c757d;
  font-size: 12px;
  font-style: italic;
}

.response-buttons {
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
}

.response-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: not-allowed;
  transition: all 0.2s ease;
  min-width: 100px;
  justify-content: center;
}

.response-btn-ok {
  background: #28a745;
  color: white;
  opacity: 0.9;
}

.response-btn-not-ok {
  background: #d65409;
  color: white;
  opacity: 0.9;
}

.preview-footer {
  background: #f8f9fa;
  padding: 12px 20px;
  border-top: 1px solid #e9ecef;
  color: #6c757d;
  text-align: center;
}

.response-type-info {
  display: block;
  margin-top: 4px;
  color: #007bff;
  font-weight: 500;
}

.message-details-preview {
  margin-bottom: 32px;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e9ecef;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-label {
  font-size: 12px;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-label i {
  margin-right: 6px;
  color: #007bff;
}

.detail-value {
  font-size: 14px;
  color: #2c3e50;
  font-weight: 500;
}

.priority-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.recipients-preview {
  margin-bottom: 32px;
}

.recipients-chips-container {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e9ecef;
}

.recipients-section-label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 12px;
  font-size: 14px;
}

.recipients-section-label i {
  margin-right: 6px;
  color: #007bff;
}

.recipients-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}

.recipient-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 20px;
  padding: 8px 14px;
  font-size: 13px;
  color: #495057;
  font-weight: 500;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.recipient-chip.cc-chip {
  background: #fff3cd;
  border-color: #ffc107;
}

.recipient-chip i {
  color: #007bff;
  font-size: 12px;
}

.recipient-chip.cc-chip i {
  color: #856404;
}

.recipient-chip:hover {
  background: #e9ecef;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.recipient-chip.cc-chip:hover {
  background: #ffe69c;
}

.no-recipients {
  text-align: center;
  padding: 24px;
  color: #6c757d;
  font-style: italic;
}

.no-recipients i {
  margin-right: 8px;
  font-size: 18px;
}

.send-options {
  margin-bottom: 24px;
}

.option-cards {
  display: grid;
  gap: 16px;
}

.option-card {
  background: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.option-card:hover {
  border-color: #007bff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.15);
}

.option-card.active {
  border-color: #007bff;
  background: linear-gradient(135deg, #e7f3ff 0%, #f0f8ff 100%);
  box-shadow: 0 4px 16px rgba(0, 123, 255, 0.2);
}

.option-card.active .option-icon {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.option-card.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #f8f9fa;
  border-color: #e8eaed;
  position: relative;
  pointer-events: none;
}

.option-card.disabled .disabled-reason {
  color: #dc3545;
  font-style: italic;
  font-size: 13px;
}

.option-card.disabled::after {
  content: "Future";
  position: absolute;
  top: 8px;
  right: 12px;
  background: #ffc107;
  color: #212529;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.option-icon {
  width: 48px;
  height: 48px;
  background: #007bff;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.option-content {
  flex: 1;
}

.option-content h5 {
  margin: 0 0 8px 0;
  color: #2c3e50;
  font-size: 16px;
}

.option-content p {
  margin: 0;
  color: #6c757d;
  font-size: 14px;
}

@media (max-width: 768px) {
  .language-tabs {
    overflow-x: auto;
  }

  .language-tab {
    padding: 10px 16px;
    font-size: 13px;
    white-space: nowrap;
  }

  .preview-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .preview-subject {
    font-size: 14px;
  }

  .recipients-chips {
    gap: 8px;
  }

  .recipient-chip {
    padding: 6px 12px;
    font-size: 12px;
  }

  .option-card {
    padding: 16px;
  }

  .option-icon {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }

  .response-buttons {
    flex-direction: column;
    gap: 8px;
  }

  .response-btn {
    width: 100%;
    max-width: 200px;
    margin: 0 auto;
    padding: 12px 16px;
  }
}
</style>
